import {
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
  Label,
  Search,
} from "@/components/ui";
import { useSnomedSearch } from "@/network/useOntologies";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { hospitalizationFormSchema } from "../schemas";

interface HospitalizationFormProps {
  onSubmit: (data: z.infer<typeof hospitalizationFormSchema>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CONDITION_SNOMED_PARENT_CODE = "404684003"; // Clinical finding

export const HospitalizationForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
}: HospitalizationFormProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: reasonCodes, isLoading: isLoadingReasonCodes } =
    useSnomedSearch({
      searchTerm,
      eclExpression: `<< ${CONDITION_SNOMED_PARENT_CODE}`,
    });

  const form = useForm<z.infer<typeof hospitalizationFormSchema>>({
    resolver: zodResolver(hospitalizationFormSchema),
    defaultValues: {
      periodStartDate: undefined,
      reasonCode: undefined,
      reasonDisplay: undefined,
      reasonSystem: undefined,
    },
  });

  return (
    <div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 px-3 py-2"
      >
        <Label>Add Hospitalization</Label>
        <div className="flex flex-row gap-2">
          <Controller
            control={form.control}
            name="periodStartDate"
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <DatePicker
                  autoFocus
                  label="Date of Hospitalization"
                  date={field.value}
                  setDate={field.onChange}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="reasonCode"
            render={({ fieldState }) => (
              <Field className="flex-1">
                <FieldLabel htmlFor="reasonCode">Reason</FieldLabel>
                <Search
                  placeholder="Search for a reason"
                  options={
                    reasonCodes?.map((code) => ({
                      label: code.display,
                      value: code.code,
                      metadata: {
                        system: code.system,
                      },
                    })) || []
                  }
                  onInputChange={(input) => {
                    setSearchTerm(input);
                  }}
                  onItemSelected={(item) => {
                    form.setValue("reasonCode", item.value as string);
                    form.setValue("reasonDisplay", item.label);
                    form.setValue(
                      "reasonSystem",
                      (item.metadata as { system?: string })?.system || "",
                    );
                  }}
                  isLoading={isLoadingReasonCodes}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </form>
    </div>
  );
};
